import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { X, Trash2, MessageCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { STATUS_LABELS, STATUS_STYLES, salaSummary, salaExclusiveItems, legacySalaToNew, FOOD_SERVICE_LABELS, GROUP_LABELS, buildClientReminderUrl } from "@/lib/quote";
import { ShiftsEditor } from "@/components/admin/ShiftsEditor";
import { SalaSelector } from "@/components/admin/quote/SalaSelector";
import { QuoteItemsEditor } from "@/components/admin/quote/QuoteItemsEditor";
import { MenuBuilder } from "@/components/admin/quote/MenuBuilder";
import { QuoteActions } from "@/components/admin/quote/QuoteActions";
import { TimelinePanel } from "@/components/admin/quote/TimelinePanel";
import { SheetsPanel } from "@/components/admin/quote/SheetsPanel";

export const QuoteDetail = ({ quoteId, onClose }) => {
  const [q, setQ] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [sheets, setSheets] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get(`/admin/quotes/${quoteId}`);
    setQ(r.data);
    if (["confermato", "completato"].includes(r.data.status)) {
      const s = await api.get(`/admin/quotes/${quoteId}/sheets`);
      setSheets(s.data);
    }
  }, [quoteId]);

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
    api.get("/admin/catalog").then((r) => setCatalog(r.data));
  }, [load]);

  const save = async (patch) => {
    try {
      const r = await api.put(`/admin/quotes/${quoteId}`, patch);
      setQ(r.data);
      toast.success("Salvato");
    } catch (e) { toast.error(apiError(e)); }
  };

  const action = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const foodSummary = useMemo(() => {
    if (!q?.food?.groups) return "";
    const counts = Object.entries(q.food.groups)
      .filter(([, c]) => Number(c?.budget_pp) > 0 || c?.service === "tavola")
      .map(([g, c]) => `${GROUP_LABELS[g]} ${FOOD_SERVICE_LABELS[c.service] || "a Buffet"}${Number(c.budget_pp) > 0 ? ` (budget ${c.budget_pp} €/pp)` : ""}`);
    return counts.join(" · ") || "a Buffet, nessun budget indicato";
  }, [q?.food]);

  if (!q) return null;

  const canSheets = ["confermato", "completato"].includes(q.status);

  const updateSala = async (patch) => {
    const cfg = (await api.get("/config")).data;
    const base = legacySalaToNew(q.sala, { rooms: "interna", excl_interna: false, excl_deorsi: false, assignment: { adulti: "interna", ragazzi: "interna", bambini: "interna" } });
    const sala = { ...base, ...patch };
    delete sala.choice;
    const items = q.items.filter((it) => it.product_id !== "sala-esclusiva");
    items.push(...salaExclusiveItems(sala, cfg));
    save({ items, sala });
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose}>
      <div data-testid="quote-detail-drawer" className="w-full max-w-2xl bg-[#0F172A] text-slate-100 h-full overflow-y-auto border-l border-slate-700" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#1E293B] border-b border-slate-700 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <div className="font-display font-bold">#{q.number} · {q.contact.name}</div>
            <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${STATUS_STYLES[q.status]}`}>{STATUS_LABELS[q.status]}</span>
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="whatsapp-reminder-button" onClick={() => window.open(buildClientReminderUrl(q), "_blank")} title="Invia promemoria WhatsApp al cliente" className="p-2 text-slate-500 hover:text-[#25D366]"><MessageCircle className="w-5 h-5" /></button>
            <button data-testid="delete-quote-button" onClick={() => { if (window.confirm(`Eliminare definitivamente il preventivo #${q.number}?`)) action(async () => { await api.delete(`/admin/quotes/${quoteId}`); onClose(); }, "Preventivo eliminato"); }} className="p-2 text-slate-500 hover:text-rose-400"><Trash2 className="w-5 h-5" /></button>
            <button data-testid="quote-detail-close" onClick={onClose} className="p-2 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-5 space-y-6">
          <div className="text-sm text-slate-300 space-y-1">
            <div><b>{q.event.type}{q.event.type_custom ? ` — ${q.event.type_custom}` : ""}</b> · {q.event.date} · {q.event.time_start}–{q.event.time_end}{q.event.extra_hours > 0 ? ` (+${q.event.extra_hours}h extra)` : ""}</div>
            <div>{q.guests.adults} adulti + {q.guests.teens || 0} ragazzi + {q.guests.children} bambini · {q.guests.dietary || "nessuna esigenza"}</div>
            {(q.sala?.rooms || q.sala?.choice) && (
              <div>
                🏛 <b>{salaSummary(q.sala)}</b>
                {q.sala.rooms === "entrambe" && q.sala.assignment && (
                  <span className="text-slate-400"> — adulti: {q.sala.assignment.adulti === "deorsi" ? "Dehors" : "Interna"}, ragazzi: {q.sala.assignment.ragazzi === "deorsi" ? "Dehors" : "Interna"}, bambini: {q.sala.assignment.bambini === "deorsi" ? "Dehors" : "Interna"}</span>
                )}
              </div>
            )}
            <div className="text-slate-400">{q.contact.phone} · {q.contact.email || "no email"}</div>
            {q.food?.groups && (
              <div>
                🍽 <b>Food:</b> {foodSummary}
                {q.food.torta && q.food.torta !== "nessuna" && <span className="text-[#F0B429] font-semibold"> · Torta: {q.food.torta === "bliss" ? "fornita da Bliss (da 30 €/kg)" : "pasticceria esterna (2 €/pp)"}</span>}
              </div>
            )}
            {(q.bar?.escludi_ragazzi || q.bar?.escludi_bambini) && (
              <div data-testid="bar-exclusions-line">🍹 <b>Bar:</b> {[q.bar.escludi_ragazzi && "ragazzi", q.bar.escludi_bambini && "bambini"].filter(Boolean).join(" e ")} esclusi dai servizi bar → Soft Drink assegnato</div>
            )}
            {!q.food?.groups && q.food?.service && (
              <div>🍽 <b>Food {FOOD_SERVICE_LABELS[q.food.service]}</b>{Number(q.food.budget_pp) > 0 && <span className="text-[#F0B429] font-semibold"> · budget {q.food.budget_pp} €/persona</span>}</div>
            )}
            {q.food?.note && <div data-testid="food-note-line">📝 <b>Preferenze food:</b> <span className="text-slate-300">{q.food.note}</span></div>}
            {q.client_accepted_at && (
              <div data-testid="client-accepted-badge" className="text-emerald-400 font-semibold">✅ Accettato dal cliente il {new Date(q.client_accepted_at).toLocaleString("it-IT")}</div>
            )}
            {q.contact.notes && <div className="text-slate-400 italic">"{q.contact.notes}"</div>}
            {q.rejection_reason && <div className="text-rose-400">Motivo rifiuto: {q.rejection_reason}</div>}
          </div>

          <SalaSelector q={q} updateSala={updateSala} />
          <QuoteItemsEditor q={q} setQ={setQ} save={save} catalog={catalog} />
          <MenuBuilder q={q} save={save} catalog={catalog} />
          <ShiftsEditor q={q} save={save} />
          <QuoteActions q={q} quoteId={quoteId} action={action} />
          {canSheets && <TimelinePanel q={q} setQ={setQ} quoteId={quoteId} action={action} />}
          {canSheets && <SheetsPanel quoteId={quoteId} action={action} sheets={sheets} setSheets={setSheets} />}
        </div>
      </div>
    </div>
  );
};
